// layoutUtils.js - Utilities for proper widget layout across screen sizes

import { getWidgetViewportPosition } from "./widgetUtils";

// Grid configurations for different screen sizes
export const LAYOUT_CONFIGS = {
  desktop: {
    totalColumns: 12,
    maxWidth: 1280,
    gapPixels: 24,
  },
  tablet: {
    totalColumns: 8,
    maxWidth: 768,
    gapPixels: 24,
  },
  mobile: {
    totalColumns: 4,
    maxWidth: 375,
    gapPixels: 24,
  },
};

/**
 * Properly calculates optimal positions for a new widget across all screen sizes
 * Ensures proper grid alignment and no overlaps
 */
export const calculateOptimalPosition = (
  existingWidgets,
  widgetColumns = 4,
  widgetHeight = 4
) => {
  const result = {};

  // Handle each layout separately with proper grid constraints
  Object.keys(LAYOUT_CONFIGS).forEach((layout) => {
    const config = LAYOUT_CONFIGS[layout];
    const position = findOptimalPositionForLayout(
      existingWidgets,
      layout,
      widgetColumns,
      widgetHeight,
      config
    );
    result[layout] = position;
  });

  return result;
};

/**
 * Finds the optimal position for a widget in a specific layout
 */
const findOptimalPositionForLayout = (
  existingWidgets,
  layout,
  requestedColumns,
  requestedHeight,
  config
) => {
  // Adjust widget dimensions for the target layout
  let columns = requestedColumns;
  let height = requestedHeight;

  if (layout === "mobile") {
    // Mobile: respect the requested columns (don't force full width)
    columns = Math.min(requestedColumns, config.totalColumns);
  } else if (layout === "tablet") {
    // Tablet: ensure widget fits within 8 columns
    columns = Math.min(requestedColumns, config.totalColumns);
  }

  // Get occupied spaces for this layout
  const occupiedSpaces = getOccupiedSpaces(existingWidgets, layout);

  // For mobile, use vertical stacking strategy
  if (layout === "mobile") {
    return findMobilePosition(occupiedSpaces, columns, height);
  }

  // For desktop and tablet, use grid-based positioning
  return findGridPosition(occupiedSpaces, columns, height, config.totalColumns);
};

/**
 * Gets all occupied spaces for widgets in a specific layout
 */
const getOccupiedSpaces = (widgets, layout) => {
  if (!widgets || !Array.isArray(widgets)) return [];

  return widgets
    .map((widget) => {
      const widgetLayout = widget.layout?.[layout];
      if (!widgetLayout) return null;

      const position = widgetLayout.position || 0;
      const columnHeight = widgetLayout.columnHeight || 4;
      const startColumn = widgetLayout.startColumn || 0;
      const columns = widgetLayout.columns || 4;

      return {
        id: widget.id,
        top: position,
        bottom: position + columnHeight,
        left: startColumn,
        right: startColumn + columns,
      };
    })
    .filter(Boolean);
};

/**
 * Finds position for mobile layout using grid positioning
 */
const findMobilePosition = (occupiedSpaces, columns, height) => {
  // For mobile, use the same grid-based positioning as other layouts
  // This allows widgets to be placed side by side if they fit
  return findGridPosition(occupiedSpaces, columns, height, 4); // 4 is mobile totalColumns
};

/**
 * Finds position for desktop/tablet layout using grid-based approach
 */
const findGridPosition = (occupiedSpaces, columns, height, totalColumns) => {
  let bestPosition = 0;
  let bestColumn = 0;
  let bestScore = Infinity;

  // Try each possible column position
  for (let startCol = 0; startCol <= totalColumns - columns; startCol++) {
    const position = findVerticalPositionForColumn(
      occupiedSpaces,
      startCol,
      columns,
      height
    );

    // Calculate score favoring top positions and left-most columns
    // Priority: top positions first, then leftmost positions
    const score = position * 100 + startCol; // Heavily weight vertical position, then prefer left

    if (score < bestScore) {
      bestScore = score;
      bestPosition = position;
      bestColumn = startCol;
    }
  }

  return {
    position: bestPosition,
    startColumn: bestColumn,
  };
};

/**
 * Finds the first available vertical position for a specific column range
 */
const findVerticalPositionForColumn = (
  occupiedSpaces,
  startCol,
  columns,
  height
) => {
  let position = 0;

  while (position < 1000) {
    // Reasonable limit
    let hasConflict = false;

    // Check for conflicts with existing widgets
    for (const space of occupiedSpaces) {
      const hasVerticalOverlap = !(
        position + height <= space.top || position >= space.bottom
      );
      const hasHorizontalOverlap = !(
        startCol + columns <= space.left || startCol >= space.right
      );

      if (hasVerticalOverlap && hasHorizontalOverlap) {
        hasConflict = true;
        position = space.bottom;
        break;
      }
    }

    if (!hasConflict) {
      return position;
    }
  }

  return position;
};

/**
 * Function to check if a widget layout has overlaps
 */
export const hasLayoutOverlaps = (widgets, layout) => {
  if (!widgets || !Array.isArray(widgets) || widgets.length <= 1) return false;

  const occupiedSpaces = getOccupiedSpaces(widgets, layout);

  // Check each pair of widgets for overlaps
  for (let i = 0; i < occupiedSpaces.length; i++) {
    for (let j = i + 1; j < occupiedSpaces.length; j++) {
      const space1 = occupiedSpaces[i];
      const space2 = occupiedSpaces[j];

      // Check for any overlap (even partial)
      const hasVerticalOverlap = !(
        space1.bottom <= space2.top || space1.top >= space2.bottom
      );
      const hasHorizontalOverlap = !(
        space1.right <= space2.left || space1.left >= space2.right
      );

      if (hasVerticalOverlap && hasHorizontalOverlap) {
        console.log(
          `[Layout Debug] Overlap detected between widgets ${space1.id} and ${space2.id} in ${layout} layout`
        );
        console.log(`Widget ${space1.id}:`, {
          top: space1.top,
          bottom: space1.bottom,
          left: space1.left,
          right: space1.right,
        });
        console.log(`Widget ${space2.id}:`, {
          top: space2.top,
          bottom: space2.bottom,
          left: space2.left,
          right: space2.right,
        });
        return true;
      }
    }
  }

  return false;
};

/**
 * Compacts and fixes layout by removing overlaps and gaps
 */
export const compactAndFixLayout = (widgets, targetLayout = "mobile") => {
  if (!widgets || !Array.isArray(widgets)) return widgets;

  const config = LAYOUT_CONFIGS[targetLayout];
  const compactedWidgets = JSON.parse(JSON.stringify(widgets));

  console.log(
    `[Layout Fix] Starting ${targetLayout} layout fix for ${widgets.length} widgets`
  );

  if (targetLayout === "mobile") {
    return fixMobileLayout(compactedWidgets, config);
  } else if (targetLayout === "tablet") {
    return fixTabletLayout(compactedWidgets, config);
  }

  return compactedWidgets;
};

/**
 * Fixes mobile layout by ensuring proper positioning
 */
const fixMobileLayout = (widgets, config) => {
  // Sort widgets by their intended order (use desktop position as reference)
  widgets.sort((a, b) => {
    const aPos =
      a.layout?.mobile?.position ||
      a.layout?.tablet?.position ||
      a.layout?.desktop?.position ||
      0;
    const bPos =
      b.layout?.mobile?.position ||
      b.layout?.tablet?.position ||
      b.layout?.desktop?.position ||
      0;
    return aPos - bPos;
  });

  // Arrange widgets vertically with proper positioning
  const occupiedSpaces = [];

  widgets.forEach((widget, index) => {
    // Ensure mobile layout exists
    if (!widget.layout.mobile) {
      widget.layout.mobile = {
        columns: Math.min(
          widget.layout.desktop?.columns || 4,
          config.totalColumns
        ),
        columnHeight: widget.layout.desktop?.columnHeight || 4,
        startColumn: 0,
      };
    }

    const mobileLayout = widget.layout.mobile;
    const columns =
      mobileLayout.columns ||
      Math.min(widget.layout.desktop?.columns || 4, config.totalColumns);
    const height =
      mobileLayout.columnHeight || widget.layout.desktop?.columnHeight || 4;

    // Find the best position that doesn't overlap
    const position = findNonOverlappingPosition(
      occupiedSpaces,
      columns,
      height,
      config.totalColumns
    );

    // Use the existing startColumn from the mobile layout
    let startColumn = mobileLayout.startColumn || 0;
    // Ensure startColumn doesn't push widget off the grid
    if (startColumn + columns > config.totalColumns) {
      startColumn = Math.max(0, config.totalColumns - columns);
    }

    // Update widget layout
    widget.layout.mobile = {
      ...mobileLayout,
      position: position.row,
      startColumn: startColumn,
      columns: columns,
      columnHeight: height,
    };

    // Add to occupied spaces
    occupiedSpaces.push({
      top: position.row,
      bottom: position.row + height,
      left: startColumn,
      right: startColumn + columns,
    });

    console.log(
      `[Mobile Fix] Widget ${index} (${widget.id}): ${startColumn}x${position.row} (${columns}x${height})`
    );
  });

  console.log(`[Mobile Fix] Fixed ${widgets.length} widgets`);
  return widgets;
};

/**
 * Fixes tablet layout by ensuring proper positioning
 */
const fixTabletLayout = (widgets, config) => {
  // Sort widgets by their intended order
  widgets.sort((a, b) => {
    const aPos = a.layout?.tablet?.position || a.layout?.desktop?.position || 0;
    const bPos = b.layout?.tablet?.position || b.layout?.desktop?.position || 0;
    return aPos - bPos;
  });

  // Arrange widgets with proper positioning
  const occupiedSpaces = [];

  widgets.forEach((widget, index) => {
    // Ensure tablet layout exists
    if (!widget.layout.tablet) {
      widget.layout.tablet = {
        columns: Math.min(
          widget.layout.desktop?.columns || 4,
          config.totalColumns
        ),
        columnHeight: widget.layout.desktop?.columnHeight || 4,
        startColumn: 0,
      };
    }

    const tabletLayout = widget.layout.tablet;
    const columns = tabletLayout.columns || Math.min(4, config.totalColumns);
    const height = tabletLayout.columnHeight || 4;

    // Find best position that doesn't overlap
    const position = findNonOverlappingPosition(
      occupiedSpaces,
      columns,
      height,
      config.totalColumns
    );

    // Update widget layout
    widget.layout.tablet = {
      ...tabletLayout,
      position: position.row,
      startColumn: position.column,
      columns: columns,
      columnHeight: height,
    };

    // Add to occupied spaces
    occupiedSpaces.push({
      top: position.row,
      bottom: position.row + height,
      left: position.column,
      right: position.column + columns,
    });

    console.log(
      `[Tablet Fix] Widget ${index} (${widget.id}): ${position.column}x${position.row} (${columns}x${height})`
    );
  });

  console.log(`[Tablet Fix] Fixed ${widgets.length} widgets`);
  return widgets;
};

/**
 * Finds a non-overlapping position for a widget
 */
const findNonOverlappingPosition = (
  occupiedSpaces,
  columns,
  height,
  totalColumns
) => {
  // Try each row, starting from 0
  for (let row = 0; row < 1000; row++) {
    // Try each possible column position in this row
    for (let col = 0; col <= totalColumns - columns; col++) {
      const proposedSpace = {
        top: row,
        bottom: row + height,
        left: col,
        right: col + columns,
      };

      // Check if this position overlaps with any existing widget
      let hasOverlap = false;
      for (const existingSpace of occupiedSpaces) {
        const hasVerticalOverlap = !(
          proposedSpace.bottom <= existingSpace.top ||
          proposedSpace.top >= existingSpace.bottom
        );
        const hasHorizontalOverlap = !(
          proposedSpace.right <= existingSpace.left ||
          proposedSpace.left >= existingSpace.right
        );

        if (hasVerticalOverlap && hasHorizontalOverlap) {
          hasOverlap = true;
          break;
        }
      }

      if (!hasOverlap) {
        return { row, column: col };
      }
    }
  }

  // Fallback: place at bottom
  const maxBottom = Math.max(0, ...occupiedSpaces.map((space) => space.bottom));
  return { row: maxBottom, column: 0 };
};

/**
 * Fixes all layouts for a set of widgets
 * This is the main function for retroactive fixes
 */
export const fixAllLayouts = (widgets) => {
  if (!widgets || !Array.isArray(widgets)) return widgets;

  let fixedWidgets = JSON.parse(JSON.stringify(widgets));

  // Fix mobile layout first (most critical)
  fixedWidgets = compactAndFixLayout(fixedWidgets, "mobile");

  // Fix tablet layout
  fixedWidgets = compactAndFixLayout(fixedWidgets, "tablet");

  console.log(
    `[Layout Fix] Applied fixes to all layouts for ${widgets.length} widgets`
  );
  return fixedWidgets;
};
